import React from 'react';
import './RichEditor.css';

// contentEditable leaves residue (e.g. "<br>", "<p><br></p>", "&nbsp;") even
// after the user clears all visible text. Treat those as empty so saves actually
// blank the field instead of persisting invisible markup.
function isHtmlBlank(html) {
  if (!html) return true;
  return !html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

/**
 * `tools` (optional) narrows the toolbar to the named buttons, for a field whose
 * renderer keeps less than this editor can make — offering a heading the reader
 * never sees is worse than not offering it. Names: bold, italic, underline,
 * strike, bullets, numbers, heading, paragraph, link, unlink, clear. Omitted ⇒
 * every button, so existing callers are unchanged.
 */
export default function RichEditor({ value, onChange, placeholder, minHeight = 120, disabled = false, tools: only = null }) {
  const editorRef = React.useRef(null);
  const isInternalChange = React.useRef(false);

  // Sync external value → DOM only when it differs (avoid cursor jump)
  React.useEffect(() => {
    const el = editorRef.current;
    if (!el || isInternalChange.current) return;
    if (el.innerHTML !== value) el.innerHTML = value || '';
  }, [value]);

  function handleInput() {
    isInternalChange.current = true;
    const html = editorRef.current.innerHTML;
    onChange(isHtmlBlank(html) ? '' : html);
    // Reset flag after React re-render
    requestAnimationFrame(() => { isInternalChange.current = false; });
  }

  function exec(cmd, arg = null) {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    handleInput();
  }

  function isActive(cmd) {
    try { return document.queryCommandState(cmd); } catch { return false; }
  }

  const allTools = [
    { name: 'bold',      cmd: 'bold',          icon: <strong>B</strong>,   title: 'Bold (Ctrl+B)'      },
    { name: 'italic',    cmd: 'italic',        icon: <em>I</em>,           title: 'Italic (Ctrl+I)'    },
    { name: 'underline', cmd: 'underline',     icon: <u>U</u>,             title: 'Underline (Ctrl+U)' },
    { name: 'strike',    cmd: 'strikeThrough', icon: <s>S</s>,             title: 'Strikethrough'      },
    { sep: true },
    { name: 'bullets', cmd: 'insertUnorderedList', icon: '≡', title: 'Bullet list' },
    { name: 'numbers', cmd: 'insertOrderedList',   icon: '1.', title: 'Numbered list' },
    { sep: true },
    { name: 'heading',   action: () => exec('formatBlock', '<h3>'), icon: 'H', title: 'Heading'         },
    { name: 'paragraph', action: () => exec('formatBlock', '<p>'),  icon: '¶', title: 'Paragraph'       },
    { sep: true },
    { name: 'link', action: () => {
        const url = window.prompt('Enter URL:');
        if (url) exec('createLink', url);
      }, icon: '🔗', title: 'Insert link' },
    { name: 'unlink', action: () => exec('unlink'), icon: '✂', title: 'Remove link' },
    { sep: true },
    { name: 'clear', action: () => exec('removeFormat'), icon: '✕', title: 'Clear formatting' },
  ];
  // Keep only the named buttons, then drop separators left leading, trailing
  // or doubled by the ones that went.
  const tools = [];
  for (const t of (only ? allTools.filter((x) => x.sep || only.includes(x.name)) : allTools)) {
    if (t.sep && (!tools.length || tools[tools.length - 1].sep)) continue;
    tools.push(t);
  }
  while (tools.length && tools[tools.length - 1].sep) tools.pop();

  return (
    <div className={`re-wrap ${disabled ? 're-disabled' : ''}`}>
      {!disabled && (
        <div className="re-toolbar">
          {tools.map((tool, i) =>
            tool.sep ? (
              <span key={i} className="re-sep"/>
            ) : (
              <button
                key={i}
                type="button"
                title={tool.title}
                className={`re-btn ${!tool.action && isActive(tool.cmd) ? 'active' : ''}`}
                onMouseDown={e => {
                  e.preventDefault(); // keep focus in editor
                  tool.action ? tool.action() : exec(tool.cmd);
                }}
              >
                {tool.icon}
              </button>
            )
          )}
        </div>
      )}
      <div
        ref={editorRef}
        className="re-body"
        contentEditable={!disabled}
        suppressContentEditableWarning
        style={{ minHeight }}
        onInput={handleInput}
        data-placeholder={placeholder || 'Write here…'}
      />
    </div>
  );
}
